import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  googleReviewUrlResultSchema,
  messageListResultSchema,
  messageResultSchema,
  messagesResultSchema,
  messageTestResultSchema,
  type CreateMessageInput,
  type GoogleReviewUrlResult,
  type MessageListResult,
  type MessageResult,
  type MessagesResult,
  type MessageTestResult,
  type SetMessageEnabledInput,
  type UpdateMessageInput,
} from '../schemas';
import { useToken } from './use-token';

/**
 * Les modèles de messages du cabinet, avec le catalogue qui les décrit.
 *
 * Le catalogue vient du serveur et n'est pas déclaré ici : c'est lui qui
 * décide quel texte part à un patient, et un déclencheur d'envoi n'a pas
 * d'écran pour aller lire une constante de navigateur.
 *
 * La première lecture crée les modèles par défaut du cabinet, éteints : il n'y
 * a donc jamais d'écran vide à gérer, ni de « créer mon premier message ».
 */
export function useMessages() {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.messages.list(),
    queryFn: () => api.get<MessagesResult>('/messages', { schema: messagesResultSchema }),
    enabled: token !== null,
  });
}

/**
 * Ajoute un cas à une catégorie qui en accepte.
 *
 * Créé éteint : l'allumage vérifie que la mise en route est complète, et c'est
 * `useSetMessageEnabled` qui s'en charge — la boîte de mise en route enchaîne
 * les deux.
 */
export function useCreateMessage() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateMessageInput) =>
      api.post<MessageResult>('/messages', input, { schema: messageResultSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.messages.all() }),
  });
}

/**
 * Retouche un modèle : nom, objet, corps, déclenchement, ciblage.
 *
 * `enabled` n'en fait pas partie — enregistrer un texte ne doit pas valoir
 * mise en production.
 */
export function useUpdateMessage() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateMessageInput & { id: string }) =>
      api.patch<MessageResult>(`/messages/${id}`, input, { schema: messageResultSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.messages.all() }),
  });
}

/** Supprime un cas. Le filet de sécurité d'une catégorie n'est pas supprimable. */
export function useDeleteMessage() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/messages/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.messages.all() }),
  });
}

/**
 * Allume ou éteint un modèle.
 *
 * Un refus arrive en `409` avec un `code` (`ApiError.code`) qui dit ce qui
 * manque : `google_review_url_required`, `reminder_send_hour_required`,
 * `care_motifs_required`. C'est ce code qui désigne la boîte à ouvrir.
 */
export function useSetMessageEnabled() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: SetMessageEnabledInput & { id: string }) =>
      api.put<MessageResult>(`/messages/${id}/enabled`, input, { schema: messageResultSchema }),
    // Les promesses sont rendues : la mutation reste « en cours » jusqu'à ce que la
    // liste soit rechargée, donc le bouton tourne jusqu'à ce que l'état change à l'écran.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.messages.all() }),
        // Le lien de la fiche Google vit avec les réglages d'envoi : l'allumage
        // des demandes d'avis peut l'avoir posé.
        queryClient.invalidateQueries({ queryKey: queryKeys.emailSettings.all() }),
      ]),
  });
}

/** Allume ou éteint tout ce qu'une catégorie contient : le bouton de la carte. */
export function useSetCategoryEnabled() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ catalogKey, enabled }: { catalogKey: string; enabled: boolean }) =>
      api.put<MessageListResult>(
        `/messages/categories/${catalogKey}/enabled`,
        { enabled },
        { schema: messageListResultSchema },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.messages.all() }),
  });
}

/**
 * Change le rang d'un cas dans sa catégorie : c'est lui qui décide lequel
 * emporte un rendez-vous que deux cas visent tous les deux.
 */
export function useMoveMessage() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: 'up' | 'down' }) =>
      api.put<MessageListResult>(
        `/messages/${id}/position`,
        { direction },
        { schema: messageListResultSchema },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.messages.all() }),
  });
}

/**
 * Envoie le message à une adresse choisie, avec des valeurs d'exemple.
 *
 * Même chemin d'envoi qu'un message à un patient : c'est ce qui fait qu'un
 * test réussi prouve quelque chose. Sans adresse d'expédition configurée,
 * l'API répond `409 smtp_not_configured`.
 */
export function useSendMessageTest() {
  const api = useApi();

  return useMutation({
    mutationFn: ({ id, recipientEmail }: { id: string; recipientEmail: string }) =>
      api.post<MessageTestResult>(
        `/messages/${id}/test`,
        { recipientEmail },
        { schema: messageTestResultSchema },
      ),
  });
}

/**
 * Enregistre le lien de la fiche Google du cabinet.
 *
 * Le serveur réécrit au passage les corps de messages qui portaient le repère
 * `{{lienAvis}}` ou l'ancien lien : le lien ne dépend pas du rendez-vous, il
 * vit dans le texte.
 */
export function useSaveGoogleReviewUrl() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (url: string) =>
      api.put<GoogleReviewUrlResult>(
        '/messages/google-review-url',
        { url },
        { schema: googleReviewUrlResultSchema },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages.all() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.emailSettings.all() });
    },
  });
}
